import {
  QuestionListExtraFilter,
  QuestionListOrdering,
  QuestionListOrderingDirection,
} from 'types/questions';
import {slugify} from 'utils/string';

export const applyQuestionListConfig = <T extends Question>(
  questions: T[],
  config: QuestionListConfig,
) => {
  const slugifiedSearchQuery = slugify(config.searchQuery);

  const date = new Date();
  date.setDate(date.getDate() - 90);
  const ninetyDaysAgo = date.toISOString();

  const filtered = questions
    .filter((question) => {
      if (slugifiedSearchQuery) {
        const matches =
          slugify(question.title).includes(slugifiedSearchQuery) ||
          question.companies.some(({company}) => slugify(company).includes(slugifiedSearchQuery)) ||
          question.type.includes(slugifiedSearchQuery);
        if (!matches) return false;
      }

      if (config.types.length > 0) {
        if (!config.types.includes(question.type)) return false;
      }

      if (config.levels.length > 0) {
        if (!config.levels.includes(question.level)) return false;
      }

      if (config.tags && config.tags.length > 0) {
        const filteredTags = question.tags.filter((c) => config.tags.includes(c.name));
        if (filteredTags.length === 0) return false;
      }

      if (config.companies.length > 0) {
        const filteredCompanies = question.companies.filter(({company}) =>
          config.companies.includes(company),
        );
        if (filteredCompanies.length === 0) return false;
      }

      if (config.positions.length > 0) {
        const filteredPositions = question.positions.filter(({position}) =>
          config.positions.includes(position),
        );
        if (filteredPositions.length === 0) return false;
      }

      if (config.extraFilters.length > 0) {
        if (config.extraFilters.includes(QuestionListExtraFilter.Saved)) {
          if (!question.saved) return false;
        }
        if (config.extraFilters.includes(QuestionListExtraFilter.Completed)) {
          if (!question.completed) return false;
        }
        if (config.extraFilters.includes(QuestionListExtraFilter.WithSolutions)) {
          if (!question.has_solution) return false;
        }
        if (config.extraFilters.includes(QuestionListExtraFilter.WithoutSolutions)) {
          if (question.has_solution) return false;
        }
        if (config.extraFilters.includes(QuestionListExtraFilter.Recent)) {
          if (question.date_asked < ninetyDaysAgo) return false;
        }
        if (config.extraFilters.includes(QuestionListExtraFilter.Popular)) {
          return question.is_promoted;
        }
      }
      return true;
    })
    .sort((q1, q2) => {
      let r = 0;
      if (config.ordering === QuestionListOrdering.Title) {
        r = q1.title.localeCompare(q2.title);
      }
      if (config.ordering === QuestionListOrdering.Score) {
        r = q1.upvotes - q2.upvotes;
      }
      if (config.ordering === QuestionListOrdering.SuccessRate) {
        r = q1.acceptance_rate - q2.acceptance_rate;
      }
      if (config.ordering === QuestionListOrdering.Difficulty) {
        r = q1.level - q2.level;
      }
      return config.orderingDirection === QuestionListOrderingDirection.ASC ? r : -r;
    });

  const paginated = filtered.slice(
    config.pageSize * config.page,
    config.pageSize * config.page + config.pageSize,
  );

  return [filtered, paginated] as const;
};

export const defaultQuestionListConfig: QuestionListConfig = {
  searchQuery: '',
  types: [],
  levels: [],
  tags: [],
  companies: [],
  positions: [],
  extraFilters: [],
  searchQuestionTag: '',
  searchCompany: '',
  ordering: QuestionListOrdering.Recommended,
  orderingDirection: QuestionListOrderingDirection.ASC,
  pageSize: 20,
  page: 0,
};

export const extraFilterOptions = [
  {value: QuestionListExtraFilter.Saved, label: "Only show questions I've saved"},
  {value: QuestionListExtraFilter.Completed, label: "Only show questions I've completed"},
  {value: QuestionListExtraFilter.WithSolutions, label: 'Only show questions with solutions'},
  {value: QuestionListExtraFilter.WithoutSolutions, label: 'Only show questions without solutions'},
  {value: QuestionListExtraFilter.Recent, label: 'Only show recent questions'},
  {value: QuestionListExtraFilter.Popular, label: 'Only show popular questions'},
];

export const orderingOptions = [
  {value: QuestionListOrdering.Recommended, label: 'Recommended'},
  {value: QuestionListOrdering.Title, label: 'Title'},
  {value: QuestionListOrdering.Score, label: 'Score'},
  {value: QuestionListOrdering.SuccessRate, label: 'Success rate'},
  {value: QuestionListOrdering.Difficulty, label: 'Difficulty'},
];

export const pageSizeOptions = [
  {value: 20, label: '20'},
  {value: 50, label: '50'},
  {value: 100, label: '100'},
];
